import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Sparkles, X } from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { Z_INDEX, zStyle } from '../../../../lib/zLayers';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import type { EmailAiAssistantPresentation } from './emailAiAssistant.types';
import { EMAIL_AI_PANEL_WIDTH_CLASS } from './emailAiAssistant.utils';

export interface EmailAiAssistantShellProps {
  open: boolean;
  onClose: () => void;
  presentation: EmailAiAssistantPresentation;
  compact?: boolean;
  isRead: boolean;
  loading: boolean;
  openInAiChatHref: string | null;
  children: React.ReactNode;
}

export const EmailAiAssistantShell: React.FC<EmailAiAssistantShellProps> = ({
  open,
  onClose,
  presentation,
  compact = false,
  isRead,
  loading,
  openInAiChatHref,
  children,
}) => {
  const { colors } = useTheme();
  const isInline = presentation === 'inline';
  const isBottomSheet = useMediaQuery('(max-width: 640px)') && !isInline;

  const drawerHeader = isInline && compact ? (
    <div className={cn('flex shrink-0 items-center justify-between gap-2 border-b px-2 py-1.5', colors.border)}>
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
        </div>
        <div className="min-w-0">
          <h2 id="email-ai-drawer-title" className={cn('text-[11px] font-semibold leading-tight', colors.text)}>
            AI Assistant
          </h2>
          <p className={cn('truncate text-[10px]', colors.textMuted)}>
            {isRead ? 'Draft & refine replies' : 'Rewrite while you compose'}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {openInAiChatHref ? (
          <Link
            to={openInAiChatHref}
            className={cn(
              'rounded-md p-1 text-indigo-600 transition-colors dark:text-indigo-400',
              colors.interactiveHover,
            )}
            title="Open in AI Chat"
            aria-label="Open in AI Chat"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Link>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className={cn(
            'rounded-md p-1 transition-colors disabled:opacity-40',
            colors.textMuted,
            colors.interactiveHover,
          )}
          aria-label="Close AI assistant"
          title="Close"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  ) : (
    <div
      className={cn(
        'relative shrink-0 border-b px-4 py-3',
        colors.border,
        'bg-gradient-to-br from-indigo-500/[0.14] via-violet-500/[0.08] to-transparent dark:from-indigo-500/20 dark:via-violet-500/12',
        isBottomSheet && 'rounded-t-2xl px-3 py-2.5',
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25',
            isBottomSheet && 'h-8 w-8',
          )}
        >
          <Sparkles className={cn('h-5 w-5', isBottomSheet && 'h-4 w-4')} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 id="email-ai-drawer-title" className={cn('text-sm font-semibold leading-tight', colors.text)}>
            AI Assistant
          </h2>
          <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
            {isRead
              ? 'Chat about this email — draft, refine, and reply'
              : 'Chat with AI to rewrite and draft your message'}
          </p>
          {openInAiChatHref ? (
            <Link
              to={openInAiChatHref}
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Open in AI Chat
              <ExternalLink className="h-3 w-3" aria-hidden />
            </Link>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className={cn(
            'shrink-0 rounded-lg p-1.5 transition-colors disabled:opacity-40',
            colors.textMuted,
            colors.interactiveHover,
          )}
          aria-label="Close AI assistant"
          title="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );

  if (!open) return null;

  const inlineWidthClass = compact ? 'w-[min(100vw,22rem)]' : EMAIL_AI_PANEL_WIDTH_CLASS;

  if (isInline) {
    return (
      <aside
        className={cn(
          'flex h-full min-h-0 flex-shrink-0 flex-col self-stretch border-r',
          inlineWidthClass,
          colors.border,
          colors.surface,
        )}
        role="complementary"
        aria-label="AI assistant"
      >
        {drawerHeader}
        {children}
      </aside>
    );
  }

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 bg-slate-950/45 backdrop-blur-[2px]',
          !loading && 'transition-opacity',
        )}
        style={zStyle(Z_INDEX.modalBackdrop)}
        onClick={loading ? undefined : onClose}
        role="presentation"
        aria-hidden
      />
      <aside
        className={cn(
          'fixed flex flex-col shadow-2xl animate-in duration-200 ease-out',
          isBottomSheet
            ? 'inset-x-0 bottom-0 top-auto max-h-[min(88vh,720px)] w-full rounded-t-2xl border-t slide-in-from-bottom'
            : cn('top-0 right-0 bottom-0 border-l slide-in-from-right', EMAIL_AI_PANEL_WIDTH_CLASS),
          colors.surface,
          colors.border,
        )}
        style={zStyle(Z_INDEX.modal)}
        role="dialog"
        aria-modal
        aria-labelledby="email-ai-drawer-title"
        aria-busy={loading}
      >
        {drawerHeader}
        {children}
      </aside>
    </>
  );
};
